import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
const caseStudies = [{
  id: 1,
  sector: 'Retail',
  title: 'Full AC installation across 12 retail units',
  outcome: '40% energy reduction',
  image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80'
}, {
  id: 2,
  sector: 'Office',
  title: 'System replacement and TM44 compliance',
  outcome: '16-year projected lifespan',
  image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80'
}, {
  id: 3,
  sector: 'Hospitality',
  title: 'Ventilation upgrade for 200-room hotel',
  outcome: 'CIBSE compliant air changes',
  image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80'
}, {
  id: 4,
  sector: 'Industrial',
  title: 'Climate control for production facility',
  outcome: '99.8% uptime achieved',
  image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80'
}, {
  id: 5,
  sector: 'Education',
  title: 'Full HVAC design and installation',
  outcome: 'Zero disruption to term time',
  image: 'https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80'
}, {
  id: 6,
  sector: 'Hospitality',
  title: 'Kitchen ventilation and front-of-house AC',
  outcome: 'Passed all EHO inspections',
  image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80'
}];
const categories = ['Show all', 'Office', 'Retail', 'Hospitality', 'Industrial', 'Education'];
export function CaseStudies() {
  const [activeCategory, setActiveCategory] = useState('Show all');
  const filteredCases = activeCategory === 'Show all' ? caseStudies : caseStudies.filter(c => c.sector === activeCategory);
  return <main className="min-h-screen font-sans text-primary-dark pt-28 bg-[#E8F0F8]" data-id="element-385">
      {/* Hero Section */}
      <section className="container mx-auto px-6 pb-16 pt-8" data-id="element-386">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start" data-id="element-387">
          <div className="lg:col-span-7" data-id="element-388">
            <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/40 mb-6 block" data-id="element-389">
              Case Studies
            </span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-5xl md:text-6xl lg:text-7xl font-serif text-primary-dark leading-[1.1] tracking-tight" data-id="element-390">
              Projects that prove it
            </motion.h1>
          </div>
          <div className="lg:col-span-5 lg:pt-14" data-id="element-391">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-392">
              Real commercial HVAC projects. Real outcomes. See how we have
              helped businesses across the UK.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="container mx-auto px-6 pb-12" data-id="element-393">
        <nav aria-label="Case study categories" className="flex flex-wrap gap-2 bg-primary-dark/5 p-1.5 rounded-full w-fit" data-id="element-394">
          {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-primary-dark text-white' : 'text-primary-dark/70 hover:text-primary-dark hover:bg-white/50'}`} data-id="element-395">
              {category}
            </button>)}
        </nav>
      </section>

      {/* Case Studies Grid */}
      <section className="container mx-auto px-6 pb-24" data-id="element-396">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12" data-id="element-397">
          <AnimatePresence mode="popLayout" data-id="element-398">
            {filteredCases.map((study, index) => <motion.article key={study.id} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="group flex flex-col h-full" data-id="element-399">
                <Link to="/#contact" className="block overflow-hidden rounded-2xl aspect-[4/3] mb-6 relative" data-id="element-400">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-401" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-primary-dark" data-id="element-402">
                    {study.sector}
                  </div>
                </Link>
                <Link to="/#contact" className="mb-4 flex-grow" data-id="element-403">
                  <h2 className="text-xl md:text-2xl font-medium text-primary-dark line-clamp-2 group-hover:text-accent transition-colors mb-3" data-id="element-404">
                    {study.title}
                  </h2>
                  <p className="text-primary-dark/60 font-medium" data-id="element-405">
                    Outcome:{' '}
                    <span className="text-primary-dark" data-id="element-406">{study.outcome}</span>
                  </p>
                </Link>
                <Link to="/#contact" className="inline-flex items-center text-sm font-medium text-accent mt-auto group-hover:text-primary-dark transition-colors" data-id="element-407">
                  View project
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-408" />
                </Link>
              </motion.article>)}
          </AnimatePresence>
        </div>
      </section>

      {/* F1 Quote Block */}
      <section className="py-16 lg:py-24 px-4 md:px-6" data-id="element-409">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="w-[95%] mx-auto bg-primary-dark rounded-3xl overflow-hidden relative" data-id="element-410">
          <div className="grid lg:grid-cols-2 min-h-[500px]" data-id="element-411">
            {/* Left — Image */}
            <div className="relative h-72 lg:h-auto" data-id="element-412">
              <img src="https://cdn.magicpatterns.com/uploads/25TP9PqASwBfhD3FYFT9Py/pasted-image.jpg" alt="Formula 1 car — AccuTemp client" className="absolute inset-0 w-full h-full object-cover" data-id="element-413" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-primary-dark/30 hidden lg:block" data-id="element-414"></div>
            </div>

            {/* Right — Quote */}
            <div className="relative flex flex-col justify-center p-10 md:p-14 lg:p-16" data-id="element-415">
              {/* Large decorative quote mark */}
              <div className="text-[220px] md:text-[280px] leading-none font-serif text-accent/10 select-none absolute -top-16 -left-4 pointer-events-none" data-id="element-416">
                "
              </div>

              <div className="relative z-10" data-id="element-417">
                <span className="text-xs uppercase tracking-[0.2em] font-medium text-accent mb-8 block" data-id="element-418">
                  Client testimonial
                </span>

                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-serif text-white leading-[1.5] mb-10 tracking-tight" data-id="element-419">
                  "In Formula One you feel every one percent. Getting air
                  quality and temperature right in our offices has been a
                  game-changer for focus and performance."
                </blockquote>

                <div className="flex items-center gap-5" data-id="element-420">
                  <div className="w-12 h-[2px] bg-accent" data-id="element-421"></div>
                  <div data-id="element-422">
                    <div className="text-white font-medium mb-0.5" data-id="element-423">
                      Team Principal & CEO
                    </div>
                    <div className="text-white/50 text-sm" data-id="element-424">Formula 1 team</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#D4DDE8] py-24" data-id="element-425">
        <div className="container mx-auto px-6 text-center" data-id="element-426">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-427">
            <h2 className="text-3xl md:text-5xl font-serif text-primary-dark mb-6" data-id="element-428">
              Have a similar project?
            </h2>
            <p className="text-primary-dark/70 mb-10 text-lg leading-relaxed" data-id="element-429">
              Our engineers are ready to design a bespoke commercial HVAC
              solution for your building.
            </p>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-430">
              Get a free quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-431" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}